import { useEffect } from 'react';

import type { Project } from '~hooks/useProjects/models';

/**
 * Triggers: `projectData` (the fetched Project entity).
 * Writes:   the Provider's `useVendorRateForDriver` state — the switch the
 *           submit-time vendor→driver copy keys on (OrderFormV3.tsx's
 *           vendor→driver derivation step) and FreightRates' "use vendor
 *           rate for driver" checkbox reads.
 * Guards:   NEW orders only (`!isEditingOrder`) — edit orders never derive a
 *           driver rate at submit, so their switch stays untouched. The
 *           derived value is the effect-time flag AND the project carrying
 *           no driver rate of its own.
 * Legacy:   the legacy form's vendor→driver derivation.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("vendor→driver switch").
 */
export function useVendorRateForDriverDerivation({
  projectData,
  isEditingOrder,
  defaultVendorRateToDriver,
  setUseVendorRateForDriver,
}: {
  projectData: Project | null;
  isEditingOrder: boolean;
  defaultVendorRateToDriver: boolean;
  setUseVendorRateForDriver: (value: boolean) => void;
}): void {
  useEffect(() => {
    if (projectData?.id && !isEditingOrder) {
      const hasDriverRate = Boolean(projectData.driverRate || projectData.driverRateType);

      setUseVendorRateForDriver(defaultVendorRateToDriver && !hasDriverRate);
    }
    // Legacy deps verbatim; the flag and setter are read via closure exactly
    // as legacy reads them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData, isEditingOrder]);
}
